import {
  collection, doc, getDocs, setDoc, deleteDoc,
  serverTimestamp, query, orderBy, Timestamp
} from 'firebase/firestore'
import { db } from './config'
import { COLLECTIONS } from '@/constants'
import type { ApiResponse } from '@/types'

const getDb = () => {
  if (!db) throw new Error('Firebase is not configured.')
  return db
}

export interface Building {
  id: string
  name: string
  createdAt?: Date
  source?: 'custom' | 'customer'
}

const fromFirestore = (id: string, data: Record<string, any>): Building => ({
  id,
  name: data.name ?? id,
  createdAt: (data.createdAt as Timestamp)?.toDate?.() ?? new Date(),
  source: data.source ?? 'custom'
})

export const buildingsService = {
  async list(): Promise<ApiResponse<Building[]>> {
    try {
      // Fetch from dedicated buildings collection
      const q = query(collection(getDb(), COLLECTIONS.BUILDINGS), orderBy('name', 'asc'))
      const snap = await getDocs(q)
      const customBuildings = snap.docs.map(d => fromFirestore(d.id, d.data()))

      // Fetch unique buildings from bsnl_customers
      const customerBuildings = await this.extractFromCustomers()

      // Merge: custom buildings take precedence, add customer buildings not in custom
      const customNames = new Set(customBuildings.map(b => b.name.toLowerCase()))
      const merged = [
        ...customBuildings,
        ...customerBuildings.filter(b => !customNames.has(b.name.toLowerCase()))
      ].sort((a, b) => a.name.localeCompare(b.name))

      return { success: true, data: merged }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch buildings' }
    }
  },

  async extractFromCustomers(): Promise<Building[]> {
    try {
      const q = query(collection(getDb(), COLLECTIONS.CUSTOMERS))
      const snap = await getDocs(q)

      const buildingMap = new Map<string, Building>()

      snap.docs.forEach(d => {
        const data = d.data()
        const buildingName = data.building?.trim()
        if (buildingName && buildingName.length > 0) {
          const normalized = buildingName.toUpperCase()
          if (!buildingMap.has(normalized)) {
            buildingMap.set(normalized, {
              id: `cust_${normalized.replace(/\s+/g, '_')}`,
              name: normalized,
              createdAt: new Date(),
              source: 'customer'
            })
          }
        }
      })

      return Array.from(buildingMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    } catch (error) {
      console.error('[Buildings] Failed to extract from customers:', error)
      return []
    }
  },

  async add(name: string): Promise<ApiResponse<Building>> {
    try {
      const id = name.trim().toUpperCase().replace(/\s+/g, '_')
      const ref = doc(getDb(), COLLECTIONS.BUILDINGS, id)
      await setDoc(ref, { name: name.trim().toUpperCase(), createdAt: serverTimestamp() }, { merge: true })
      return { success: true, data: { id, name: name.trim().toUpperCase() } }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to add building' }
    }
  },

  async remove(id: string): Promise<ApiResponse<void>> {
    try {
      await deleteDoc(doc(getDb(), COLLECTIONS.BUILDINGS, id))
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete building' }
    }
  },
}

export default buildingsService
